<template>
  <PmTableRowPure :class="componentClass.root" :disabled="isDisabled">
    <template #cell(status)>
      <PmStatusPillPure
        type="resourceAllocation"
        :resource-allocation-status="status"
      />
    </template>
    <template #cell(project)>{{ id }} {{ project ?? '—' }} </template>
    <template #cell(job)> {{ job ?? '—' }} </template>
    <template #cell(appointment)>
      <template v-if="hasAppointments === false"> — </template>

      <template v-if="hasAppointments && appointments">
        <template v-if="appointments.length > 1">
          <PmRichTextPure>
            <ul>
              <li
                v-for="appointment in appointments"
                :key="appointment.name"
                :class="componentClass.element('appointment')"
              >
                {{ appointment.name }}<br />
                <div :class="componentClass.element('small')">
                  {{
                    startEndDateForText(
                      appointment.startDate,
                      appointment.endDate
                    )
                  }}
                </div>
              </li>
            </ul>
          </PmRichTextPure>
        </template>

        <template v-else>
          <div :class="componentClass.element('appointment')">
            {{ appointments[0].name }}<br />
            <div :class="componentClass.element('small')">
              {{
                startEndDateForText(
                  appointments[0].startDate,
                  appointments[0].endDate
                )
              }}
            </div>
          </div>
        </template>
      </template>
    </template>
    <template #cell(resourceFunction)> {{ resourceFunction ?? '—' }} </template>
    <template #cell(resonForIgnore)>
      <span class="PmMultipleResourceAllocationsRequestItemPure-small">
        <template v-if="reasonForIgnore === 'statusIsNotConsidered'">
          Status ist nicht „Angedacht”
        </template>

        <template v-if="reasonForIgnore === 'resourceAllocationExistsAlready'">
          Es besteht eine Anfrage
        </template>
      </span>
    </template>
  </PmTableRowPure>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

const COMPONENT_NAME = 'PmMultipleResourceAllocationsRequestItemPure'

export const propTypes = {
  status: {
    allowed: Object.values(STATUS_RESOURCE_ALLOCATION),
  },
  reasonForIgnore: {
    allowed: ['statusIsNotConsidered', 'resourceAllocationExistsAlready'],
  },
} as const

export default defineComponent({
  name: COMPONENT_NAME,
})
</script>

<script setup lang="ts">
import { computed } from 'vue'
import type { Nilable } from '@/types/misc'

import {
  type ResourceAllocationStatus,
  STATUS_RESOURCE_ALLOCATION,
} from '@/constants/persoplan'

import PmTableRowPure from '@/components/basics/PmTable/PmTableRowPure.vue'
import PmStatusPillPure from '@/components/persoplan/PmStatusPill/PmStatusPillPure.vue'
import PmRichTextPure from '@/components/basics/PmRichText/PmRichTextPure.vue'
import { startEndDateForText } from '@/utilities/date'
import { useComponentClass } from '@thomasaull-shared/composables'

export type Appointment = {
  name: string
  startDate: Nilable<Date>
  endDate: Nilable<Date>
}

export interface Props {
  id: number
  status?: ResourceAllocationStatus
  project?: Nilable<string>
  job?: Nilable<string>
  appointments?: Nilable<Appointment[]>
  resourceFunction?: Nilable<string>
  reasonForIgnore?: (typeof propTypes.reasonForIgnore.allowed)[number]
}

const props = withDefaults(defineProps<Props>(), {})
const componentClass = useComponentClass()

const hasAppointments = computed(() => {
  return (props.appointments?.length ?? 0) > 0
})

const isDisabled = computed(() => {
  return props.reasonForIgnore ? true : false
})
</script>

<style lang="scss">
.PmMultipleResourceAllocationsRequestItemPure {
  $block: &;

  &-statusIcon {
    width: 20px;
    height: 20px;

    &--error {
      color: color.$danger-600;
    }

    &--success {
      color: color.$success-600;
    }
  }

  &-appointment {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &-small {
    @include mixin.textLabel;
  }
}
</style>
